import { Link, useInRouterContext } from 'react-router'
import { isConsoleLinkPath } from '@parkify/api-types/console-links'
import { type AuditEntry } from '../../utils/audit'
import { averyKey, STORAGE_KEY, SEEN_NOTIF_KEY, OPTIM_SNOOZED_UNTIL_KEY, OPTIM_SNOOZE_MS, EXTEND_IDEM_KEY, type Message, type Proposal } from './types'
import { fmtMoney } from '../../utils/money'

// Build the Avery audit stamp for an executed proposal. before/after/delta are
// canonical CENTS; delta is derived (after - before) only when BOTH are finite
// numbers — otherwise null (a non-money action, or a brand-new value with no
// "before"). The Worker re-validates and sets schema + approvedAt.
export function averyStamp(
  actionType: string,
  opts?: { zoneId?: string | null; before?: number | null; after?: number | null; baseline?: unknown },
  // Additive (PR 15): when the executed action came from a stored proposal, pass
  // it so its server identity rides the audit stamp. The Worker promotes the
  // stamp to schema v2 iff `id` is present; with the proposal store off these
  // are undefined and the stamp is byte-identical (v1) to before.
  proposal?: { id?: string; correlationId?: string; kind?: string },
): AuditEntry['averyStamp'] {
  const fin = (v: number | null | undefined): number | null =>
    typeof v === 'number' && Number.isFinite(v) ? v : null
  const before = fin(opts?.before)
  const after = fin(opts?.after)
  return {
    actionType,
    zoneId: opts?.zoneId ?? null,
    before,
    after,
    delta: before !== null && after !== null ? after - before : null,
    baseline: (opts?.baseline ?? null) as Record<string, unknown> | null,
    ...(proposal?.id ? { proposalId: proposal.id } : {}),
    ...(proposal?.correlationId ? { correlationId: proposal.correlationId } : {}),
    ...(proposal?.kind ? { kind: proposal.kind } : {}),
  }
}

// A stable idempotency key for one proposal CARD (msgId:idx). Generated once and
// persisted, so retrying the same card — even after a reload — sends the same key.
// The operator extend route requires a key and the Worker replays the cached
// result for a key it has already charged, so this is what makes the "Retry"
// button safe on a money-moving (off-session card charge) action.
export function extendIdempotencyKey(cardId: string): string {
  let map: Record<string, string> = {}
  try { map = JSON.parse(localStorage.getItem(EXTEND_IDEM_KEY) || '{}') as Record<string, string> } catch { /* ignore */ }
  if (!map[cardId]) {
    map[cardId] = crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`
    try { localStorage.setItem(EXTEND_IDEM_KEY, JSON.stringify(map)) } catch { /* ignore */ }
  }
  return map[cardId]
}

// Build a short human-readable one-liner for the card header. For action
// proposals (refund/resolve/revoke) it's the relevant id. For config-edit
// proposals it's the diff so the operator can sanity-check before clicking
// Execute (e.g. "$4.00 → $5.50").
// Every human-meaningful fact a customer-fee config states, as label → display
// string. The card diffs THESE rather than raw keys, for two reasons: it reads
// like a sentence ("permits 18% → 10%"), and it makes an UNINTENDED change
// visible. The failure this card exists to catch is a monthly reprice quietly
// dragging the session rate along with it, and you only see that if the fields
// that DIDN'T move are being compared too.
//
// Sessions and permits are listed as separate facts on purpose — they are
// separate price scales (api/src/rates/fees.ts feeConfigForProduct), and one
// number labelled "service charge" reads as covering both.
//
// '—' means "not set". Deliberately the same token the money formatter uses for
// a malformed amount: an LLM-shaped payload can carry either, and on a fee card
// both mean "don't believe there is a number here."
function customerFeeFacts(f: unknown): Record<string, string> {
  const cfg = (f ?? {}) as Record<string, unknown>
  const monthly = (cfg.monthly ?? {}) as Record<string, unknown>
  const pct = (bps: unknown): string =>
    typeof bps === 'number' && Number.isFinite(bps) ? `${bps / 100}%` : '—'
  const usd = (cents: unknown): string =>
    typeof cents === 'number' && Number.isFinite(cents) ? fmtMoney(cents) : '—'
  const flag = (v: unknown): string => (v === true ? 'yes' : 'no')
  return {
    'service charge': pct(cfg.serviceChargeBps),
    'session floor': usd(cfg.serviceChargeMinCents),
    'session cap': usd(cfg.serviceChargeMaxCents),
    'admin fee': usd(cfg.adminFeeCents),
    permits: pct(monthly.serviceChargeBps),
    'permit floor': usd(monthly.serviceChargeMinCents),
    'permit cap': usd(monthly.serviceChargeMaxCents),
    absorbed: flag(cfg.serviceChargeAbsorbed),
    'card pass-through': flag(cfg.serviceFeePassThrough),
    'operator pays processing': flag(cfg.operatorPaysProcessing),
  }
}

export function summarizeProposal(p: Proposal): string {
  // Guards the unknown before delegating: proposal payloads are LLM-shaped, so a
  // non-numeric amount is possible and must read as "—", not "$0.00".
  const fmt = (cents: unknown): string => {
    if (typeof cents !== 'number' || !Number.isFinite(cents)) return '—'
    return fmtMoney(cents)
  }
  const payload = p.payload
  // Route-backed kinds are not summarized here: ProposalCard renders their
  // registry preview (routeActions.ts), and they fall to the default.
  switch (p.kind) {
    case 'refund': {
      const amt = payload.amountCents
      // Partial refund → show the amount + a "(partial)" tag so the operator
      // sees it's not the full charge before clicking Execute. Full refund
      // (no amountCents) keeps the bare txnId.
      return typeof amt === 'number' && Number.isFinite(amt)
        ? `${String(payload.txnId ?? '—')} · ${fmt(amt)} (partial)`
        : String(payload.txnId ?? '—')
    }
    case 'resolve_ticket': {
      const outcome = payload.outcome
      return typeof outcome === 'string' && outcome
        ? `${String(payload.threadId ?? '—')} · ${outcome}`
        : String(payload.threadId ?? '—')
    }
    case 'reopen_ticket':   return String(payload.threadId ?? '—')
    case 'escalate_ticket': return String(payload.threadId ?? '—')
    case 'resync_plate': {
      const ticketId = payload.ticketId
      return typeof ticketId === 'string' && ticketId
        ? `${String(payload.plate ?? '—')} · ticket ${ticketId}`
        : String(payload.plate ?? '—')
    }
    case 'update_rates_base':
      return `${String(payload.zoneName ?? payload.zoneId ?? '—')} · ${fmt(payload.currentAmountCents)} → ${fmt(payload.newAmountCents)}`
    case 'update_rates_event':
      return `${String(payload.eventId ?? '—')} · ${fmt(payload.currentAmountCents)} → ${fmt(payload.newAmountCents)}`
    case 'add_zone_photo':
      return `${String(payload.zoneName ?? payload.zoneId ?? '—')} ← ${String(payload.photoUrl ?? '')}`
    case 'add_event': {
      const start = String(payload.startDateTime ?? '').slice(0, 16).replace('T', ' ')
      const base = `${String(payload.eventTitle ?? '—')} · ${start} · ${fmt(payload.amountCents)}`
      // The server flags existing event rates in the same window (usually a
      // duplicate). Say so on the card, before Execute.
      const overlaps = Array.isArray(payload.overlaps) ? payload.overlaps as Array<{ eventTitle?: unknown; amountCents?: unknown }> : []
      if (!overlaps.length) return base
      const names = overlaps.slice(0, 2).map(o => `"${String(o.eventTitle ?? 'event')}" ${fmt(o.amountCents)}`).join(', ')
      const more = overlaps.length > 2 ? ` +${overlaps.length - 2} more` : ''
      return `${base} · ⚠ overlaps ${names}${more}`
    }
    case 'add_time_of_day_rate': {
      const win = (payload.etWindow ?? {}) as { from?: string; to?: string }
      return `${String(payload.zoneName ?? payload.zoneId ?? '—')} · daily ${win.from ?? '?'}–${win.to ?? '?'} ET · ${fmt(payload.baseAmountCents)} → ${fmt(payload.surgeAmountCents)}`
    }
    case 'set_day_of_week_rate': {
      const win = payload.window as { from?: string; to?: string } | null | undefined
      const when = win?.from ? ` ${win.from}–${win.to}` : ' all day'
      const unit = payload.baseType === 'flat' ? ' flat' : '/hr'
      // A graduated base has no single price to show a from→to against, so the
      // card states the new rate alone rather than "$0.00 → $6.00".
      const price = typeof payload.baseAmountCents === 'number'
        ? `${fmt(payload.baseAmountCents)} → ${fmt(payload.amountCents)}${unit}`
        : `${fmt(payload.amountCents)}${unit} (over graduated base)`
      return `${String(payload.zoneName ?? payload.zoneId ?? '—')} · ${String(payload.daysLabel ?? 'selected days')}${when} · ${price}`
    }
    case 'set_pass': {
      const pass = (payload.pass ?? {}) as Record<string, unknown>
      const v = (pass.validity ?? {}) as { kind?: string; minutes?: number; untilTime?: string }
      const validity = v.kind === 'duration' ? `valid ${Number(v.minutes)} min` : `out by ${String(v.untilTime ?? '?')}`
      const oversize = typeof pass.oversizeCents === 'number' ? ` · oversize ${fmt(pass.oversizeCents)}` : ''
      const retired = pass.active === false ? ' · retired' : ''
      return `${String(payload.zoneName ?? payload.zoneId ?? '—')} · ${String(pass.label ?? 'pass')} · ${fmt(pass.amountCents)} · ${validity}${oversize}${retired}`
    }
    case 'set_demand_tiers': {
      const tiers = (Array.isArray(payload.tiers) ? payload.tiers : []) as Array<{ uptoCount?: number; amountCents?: number }>
      const bands = tiers.map(t => `≤${Number(t.uptoCount)} ${fmt(t.amountCents)}`).join(', ')
      return `${String(payload.zoneName ?? payload.zoneId ?? '—')} · ${tiers.length} bands · ${bands}`
    }
    case 'remove_time_of_day_rate':
      return `${String(payload.zoneName ?? payload.zoneId ?? '—')} · undo time-of-day surge${payload.undoOf ? ' (auto-applied)' : ''}`
    case 'cancel_subscription':
      return `${String(payload.plate ?? '—')} · ${String(payload.planLabel ?? '')} ${fmt(payload.amountCents)}/mo → cancel @ ${String(payload.currentPeriodEnd ?? 'period end').slice(0, 10)}`
    case 'change_subscription_plan':
      return `${String(payload.plate ?? '—')} · ${String(payload.fromPlanLabel ?? '')} → ${String(payload.toPlanLabel ?? '')} (${fmt(payload.fromAmountCents)} → ${fmt(payload.toAmountCents)}/mo)`
    case 'set_base_rate': {
      const unit = payload.type === 'flat' ? ' flat' : '/hr'
      const min = typeof payload.minDurationMinutes === 'number' && payload.minDurationMinutes > 0
        ? ` · min ${Number(payload.minDurationMinutes)}m` : ''
      return `${String(payload.zoneName ?? payload.zoneId ?? '—')} · first base ${fmt(payload.amountCents)}${unit}${min}`
    }
    case 'set_graduated_schedule':
      return `${String(payload.zoneName ?? payload.zoneId ?? '—')} · ${String(payload.scheduleLabel ?? 'graduated')}`
    case 'set_min_duration':
      return `${String(payload.zoneName ?? payload.zoneId ?? '—')} · ${String(payload.label ?? 'minimum duration')}`
    case 'set_duration_ladder': {
      // The card has one summary line, so the notes collapse to a count here;
      // Avery relays them in full in the chat reply.
      const notes = Array.isArray(payload.notes) ? payload.notes.length : 0
      const list = payload.clear === true
        ? 'back to default durations'
        : String(payload.label ?? (Array.isArray(payload.durationLadderMinutes) ? `${payload.durationLadderMinutes.join(', ')} min` : 'durations'))
      return `${String(payload.zoneName ?? payload.zoneId ?? '—')} · ${list}${notes > 0 ? ` · ${notes} note${notes === 1 ? '' : 's'}, see reply` : ''}`
    }
    case 'set_rate_product': {
      // One line: every window with its tiles, plus what it deactivates. The
      // notes collapse to a count; Avery relays them in full in the reply.
      const retired = Array.isArray(payload.retired) ? (payload.retired as Array<{ label?: unknown }>).map(r => String(r.label ?? '')) : []
      const notes = Array.isArray(payload.notes) ? payload.notes.length : 0
      return `${String(payload.zoneName ?? payload.zoneId ?? '—')} · ${String(payload.label ?? 'rate windows')}` +
        `${retired.length > 0 ? ` · deactivates ${retired.join(', ')}` : ''}` +
        `${notes > 0 ? ` · ${notes} note${notes === 1 ? '' : 's'}, see reply` : ''}`
    }
    case 'extend_session':
      return `${String(payload.plate ?? '—')} · +${Number(payload.additionalMinutes)} min · ${fmt(payload.chargeCents)}`
    case 'update_zone_config': {
      const changed = Object.keys(payload).filter(k => k !== 'zoneId' && k !== 'zoneName')
      return `${String(payload.zoneName ?? payload.zoneId ?? '—')} · ${changed.length ? changed.join(', ') : 'no fields changed'}`
    }
    case 'create_zone':
      return `New zone · ${String(payload.name ?? '—')}`
    case 'upsert_validation': {
      const type = String(payload.type ?? '')
      const val = payload.value
      const disc =
        type === 'full' ? '100% off'
        : type === 'percent' ? `${Number(val)}% off`
        : type === 'flat' ? `${fmt(val)} off`
        : type === 'minutes' ? `${Number(val)} min free`
        : type === 'freemax' ? `${Number(val)} min free (≤${fmt(payload.capCents)})`
        : type === 'until' ? `free until ${String(payload.untilTime ?? '—')}`
        : type
      const bound =
        payload.inventoryCap != null ? `cap ${Number(payload.inventoryCap)}`
        : payload.expiresAt ? `expires ${String(payload.expiresAt).slice(0, 10)}`
        : 'no limit'
      // Say which write Execute performs: only an explicit edit may change a
      // live code; everything else is created and refused on a collision.
      const action = payload.mode === 'edit' ? 'Edit' : 'New'
      return `${action} · ${String(payload.code ?? '—')} · ${disc} · ${bound}`
    }
    case 'update_zone_fees': {
      // Until this case existed EVERY zone-fee card fell through to the default
      // '—', so the operator confirming a fee change saw Avery's free-text
      // reason and no verified statement of what would happen. On a card that
      // sets what drivers are charged, that is the gate not doing its job.
      const zone = String(payload.zoneName ?? payload.zoneId ?? '—')
      const next = customerFeeFacts(payload.customerFees)
      // Older stored proposals predate `previousCustomerFees`. No before-state
      // → state the resulting config rather than inventing a diff against
      // nothing (which would read as "everything changed").
      if (payload.previousCustomerFees === undefined) {
        const on = Object.entries(next)
          .filter(([, v]) => v !== '—' && v !== 'no')
          .map(([k, v]) => `${k} ${v}`)
        return `${zone} · ${on.length ? on.join(', ') : 'no add-on fees'}`
      }
      const prev = customerFeeFacts(payload.previousCustomerFees)
      const changed = Object.keys(next)
        .filter(k => next[k] !== prev[k])
        .map(k => `${k} ${prev[k]} → ${next[k]}`)
      // "no change" is worth saying out loud: a card that would do nothing is
      // itself the signal that the request didn't land the way it was meant to.
      return `${zone} · ${changed.length ? changed.join(' · ') : 'no change'}`
    }
    case 'delete_validation':
      return String(payload.code ?? '—')
    case 'set_contract_terms': {
      const feeBps = payload.platformFeeBps
      const fee = typeof feeBps === 'number' && Number.isFinite(feeBps) ? `${(feeBps / 100).toFixed(feeBps % 100 === 0 ? 0 : 1)}% fee` : null
      const term = payload.contractTerm === 'custom'
        ? String(payload.contractTermDescription ?? 'custom term')
        : typeof payload.contractTerm === 'string' ? payload.contractTerm : null
      const start = typeof payload.contractStartDate === 'string' && payload.contractStartDate ? `starts ${payload.contractStartDate}` : null
      const bits = [fee, term, start].filter((b): b is string => b !== null)
      return `${String(payload.clientName ?? payload.clientId ?? '—')} · ${bits.length ? bits.join(' · ') : 'no terms on this event'}`
    }
    default: return '—'
  }
}

export function rid(): string { return Math.random().toString(36).slice(2, 10) }

export function loadFromStorage(): Message[] {
  try {
    const raw = localStorage.getItem(averyKey(STORAGE_KEY))
    if (!raw) return []
    const parsed = JSON.parse(raw) as Message[]
    if (!Array.isArray(parsed)) return []
    return parsed
  } catch { return [] }
}

export function loadSeenNotifications(): Set<string> {
  try {
    const raw = localStorage.getItem(averyKey(SEEN_NOTIF_KEY))
    if (!raw) return new Set()
    const arr = JSON.parse(raw) as string[]
    return new Set(Array.isArray(arr) ? arr : [])
  } catch { return new Set() }
}

export function saveSeenNotifications(seen: Set<string>): void {
  try {
    // Cap to 100 most recent so localStorage doesn't grow forever
    const arr = Array.from(seen).slice(-100)
    localStorage.setItem(averyKey(SEEN_NOTIF_KEY), JSON.stringify(arr))
  } catch { /* ignore */ }
}

export function optimSnoozeActive(): boolean {
  try {
    const until = localStorage.getItem(OPTIM_SNOOZED_UNTIL_KEY)
    if (!until) return false
    const expires = new Date(until).getTime()
    if (Date.now() < expires) return true
    localStorage.removeItem(OPTIM_SNOOZED_UNTIL_KEY)
    return false
  } catch { return false }
}

export function setOptimSnooze(): void {
  try {
    const until = new Date(Date.now() + OPTIM_SNOOZE_MS).toISOString()
    localStorage.setItem(OPTIM_SNOOZED_UNTIL_KEY, until)
  } catch { /* ignore */ }
}

export function askPrefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true
}

// A failed chat turn is stored as `_Error: …_`. That text is also the turn's
// history sent with the next message, so it stays as is; only the bubble drops
// the underscores, which this renderer does not treat as emphasis.
export const errorDisplayText = (content: string): string => content.replace(/^_(Error: [\s\S]*)_$/, '$1')

export function renderMarkdown(text: string): JSX.Element {
  const lines = text.split('\n')
  return (
    <>
      {lines.map((line, i) => {
        const bullet = line.match(/^[-*]\s+(.*)$/)
        if (bullet) {
          return (
            <div key={i} style={{ paddingLeft: 16, position: 'relative' }}>
              <span style={{ position: 'absolute', left: 4 }}>•</span>
              {renderInline(bullet[1] ?? '')}
            </div>
          )
        }
        if (!line.trim()) return <div key={i} style={{ height: 6 }} />
        return <div key={i}>{renderInline(line)}</div>
      })}
    </>
  )
}

export function renderInline(text: string): JSX.Element {
  const parts: Array<{ kind: 'text' | 'code' | 'bold' | 'link'; value: string; href?: string }> = []
  let remaining = text
  // A link is `[label](/path)`. It renders as a link ONLY when the console
  // allowlist accepts the path (@parkify/api-types/console-links); Avery's text
  // is model output, so every link is re-checked here, never trusted. A
  // rejected token falls through as plain text, label and all.
  const re = /(`[^`]+`|\*\*[^*]+\*\*|\[[^\]\n]{1,60}\]\([^)\s]{1,300}\))/
  while (remaining.length) {
    const m = remaining.match(re)
    if (!m || m.index === undefined) { parts.push({ kind: 'text', value: remaining }); break }
    if (m.index > 0) parts.push({ kind: 'text', value: remaining.slice(0, m.index) })
    const tok = m[0]
    if (tok.startsWith('`')) parts.push({ kind: 'code', value: tok.slice(1, -1) })
    else if (tok.startsWith('**')) parts.push({ kind: 'bold', value: tok.slice(2, -2) })
    else {
      const lm = tok.match(/^\[([^\]]+)\]\(([^)]+)\)$/)
      const label = lm?.[1] ?? tok
      const href = lm?.[2] ?? ''
      if (isConsoleLinkPath(href)) parts.push({ kind: 'link', value: label, href })
      else parts.push({ kind: 'text', value: tok })
    }
    remaining = remaining.slice(m.index + tok.length)
  }
  return (
    <>
      {parts.map((p, i) => {
        if (p.kind === 'code') return <code key={i} style={{ fontFamily: 'monospace', background: 'rgba(0,0,0,0.08)', padding: '0 4px', borderRadius: 3, fontSize: 11 }}>{p.value}</code>
        if (p.kind === 'bold') return <strong key={i}>{p.value}</strong>
        if (p.kind === 'link' && p.href) return <ConsoleLink key={i} to={p.href} label={p.value} />
        return <span key={i}>{p.value}</span>
      })}
    </>
  )
}

const LINK_STYLE = {
  color: 'var(--parkify-accent)',
  fontWeight: 600,
  textDecoration: 'underline',
  textUnderlineOffset: 2,
} as const

/** In-app navigation inside the console's router (the chat keeps its state);
 *  a plain same-origin anchor where no router is mounted. */
function ConsoleLink({ to, label }: { to: string; label: string }): JSX.Element {
  const inRouter = useInRouterContext()
  if (inRouter) return <Link to={to} style={LINK_STYLE} data-avery-link="">{label}</Link>
  return <a href={to} style={LINK_STYLE} data-avery-link="">{label}</a>
}

export function AveryAvatar({ size }: { size: number }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: size / 3,
      background: 'linear-gradient(135deg, var(--parkify-accent) 0%, var(--parkify-accent-strong) 100%)',
      color: '#fff',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontWeight: 800, fontSize: Math.round(size * 0.5),
      fontFamily: 'monospace',
      boxShadow: '0 1px 4px color-mix(in srgb, var(--parkify-accent) 30%, transparent)',
      flexShrink: 0,
      textTransform: 'lowercase',
    }}>a</div>
  )
}
